import React, { useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, CreditCard, Wifi, WifiOff, CheckCircle2, RefreshCw } from 'lucide-react';

// Admin → Venue → Payments. Shows every Square Terminal paired to the account
// with its live status, and lets an admin choose the one the venue POS pushes
// payments to by default.

export default function TerminalSettingsPanel() {
  const [devices, setDevices] = useState([]);
  const [defaultId, setDefaultId] = useState('');
  const [qrMinutes, setQrMinutes] = useState(5);
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [devRes, rows] = await Promise.all([
        base44.functions.invoke('squareDevices', {}),
        base44.entities.VenuePaymentSettings.list('-created_date', 1),
      ]);
      if (devRes.data?.success) setDevices(devRes.data.devices || []);
      else setError(devRes.data?.error || 'Could not reach Square.');

      const row = rows?.[0] || null;
      setRecordId(row?.id || null);
      setDefaultId(row?.default_terminal_id || '');
      setQrMinutes(Number(row?.qr_expiry_minutes) || 5);
    } catch (e) {
      setError(e?.message || 'Could not load the payment settings.');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async (nextDefaultId, nextMinutes) => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const patch = {
        default_terminal_id: nextDefaultId,
        default_terminal_name: devices.find((d) => d.id === nextDefaultId)?.name || '',
        qr_expiry_minutes: Number(nextMinutes) || 5,
      };
      if (recordId) {
        await base44.entities.VenuePaymentSettings.update(recordId, patch);
      } else {
        const created = await base44.entities.VenuePaymentSettings.create(patch);
        setRecordId(created.id);
      }
      setSaved(true);
    } catch (e) {
      setError(e?.message || 'Could not save the payment settings.');
    }
    setSaving(false);
  };

  const chooseTerminal = (id) => {
    const next = defaultId === id ? '' : id;
    setDefaultId(next);
    save(next, qrMinutes);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-white/40 text-sm py-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking the paired terminals…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-primary" />
        <p className="text-white/40 text-sm">
          Choose which Square Terminal the venue POS sends payments to. Staff can still move a single
          sale to another paired terminal at the till.
        </p>
      </div>

      {error && (
        <div className="border border-red-500/30 bg-red-500/5 px-4 py-3 text-red-300 text-xs">{error}</div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-primary text-[10px] font-bold tracking-widest">PAIRED TERMINALS</p>
          <button
            onClick={load}
            className="flex items-center gap-2 text-white/40 hover:text-white text-[10px] font-bold tracking-widest transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> REFRESH STATUS
          </button>
        </div>

        {devices.length === 0 ? (
          <div className="border border-dashed border-white/10 bg-[#111115] px-5 py-10 text-center">
            <CreditCard className="w-8 h-8 text-white/10 mx-auto mb-3" />
            <p className="text-white/40 text-sm font-bold tracking-widest">NO TERMINALS FOUND</p>
            <p className="text-white/25 text-xs mt-2 max-w-sm mx-auto leading-relaxed">
              Pair a Square Terminal to this account and sign it in to the venue's location, then
              refresh. A Bluetooth card reader cannot be used here — it only works inside Square's own
              apps.
            </p>
          </div>
        ) : (
          <div className="border border-white/5">
            {devices.map((d) => {
              const isDefault = d.id === defaultId;
              return (
                <div
                  key={d.id}
                  className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-white/5 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-bold text-sm truncate">{d.name}</p>
                    <p className="text-white/30 text-xs mt-0.5">{d.model || 'Square Terminal'}</p>
                  </div>

                  <span
                    className={`flex items-center gap-1.5 text-[10px] font-bold tracking-widest ${
                      d.offline ? 'text-amber-300' : 'text-green-400'
                    }`}
                  >
                    {d.offline ? <WifiOff className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
                    {d.offline ? 'OFFLINE' : 'ONLINE'}
                  </span>

                  <button
                    onClick={() => chooseTerminal(d.id)}
                    disabled={saving}
                    className={`text-[10px] font-bold tracking-widest px-4 py-2.5 border transition-colors disabled:opacity-40 ${
                      isDefault
                        ? 'border-primary text-primary'
                        : 'border-white/15 text-white/50 hover:text-white hover:border-white/35'
                    }`}
                  >
                    {isDefault ? 'DEFAULT' : 'SET AS DEFAULT'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-white/30 text-xs mt-3">
          An offline terminal can still be selected, but the POS will flag it at the till until it is
          back online.
        </p>
      </div>

      <div className="max-w-sm">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-3">SCAN-TO-PAY CODE LIFETIME</p>
        <label className="flex items-center gap-3">
          <input
            type="number"
            min={1}
            max={15}
            value={qrMinutes}
            onChange={(e) => setQrMinutes(e.target.value)}
            onBlur={() => save(defaultId, qrMinutes)}
            className="seo-input w-24"
          />
          <span className="text-white/40 text-sm">minutes before the code withdraws itself</span>
        </label>
        <p className="text-white/30 text-xs mt-2">
          When a code expires it is voided and can no longer be paid. Staff can issue a fresh one from
          the till with a single tap.
        </p>
      </div>

      {saving && (
        <p className="flex items-center gap-2 text-white/40 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
        </p>
      )}
      {saved && !saving && (
        <p className="flex items-center gap-1.5 text-green-500 text-xs font-bold">
          <CheckCircle2 className="w-3.5 h-3.5" /> Saved
        </p>
      )}
    </div>
  );
}